import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Switch } from '@/components/ui/switch';
import { Search } from 'lucide-react';
import { useSuperadminDashboard, setTenantStatus, setTenantPlan, setTenantPilotMode, setTenantDemoMode, type TenantSummary } from '@/hooks/useSuperadmin';
import { useToast } from '@/hooks/use-toast';
import { format } from 'date-fns';
import { DemoDataDialog } from '@/components/superadmin/DemoDataDialog';

const SuperadminTenants = () => {
  const { tenants, loading, refetch } = useSuperadminDashboard();
  const [search, setSearch] = useState('');
  const { toast } = useToast();
  const [confirmTarget, setConfirmTarget] = useState<TenantSummary | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [demoDataTarget, setDemoDataTarget] = useState<TenantSummary | null>(null);
  // Only used to confirm turning demo mode ON for a tenant that isn't
  // already a demo — that's the direction that exposes a potentially real
  // tenant to the demo-data tooling and the built-in mock-data fallback.
  const [enableDemoTarget, setEnableDemoTarget] = useState<TenantSummary | null>(null);

  const handleConfirmStatusChange = async () => {
    if (!confirmTarget) return;
    const nextStatus = confirmTarget.status === 'active' ? 'suspended' : 'active';
    setBusyId(confirmTarget.id);
    try {
      await setTenantStatus(confirmTarget.id, nextStatus);
      toast({ title: `${confirmTarget.name} ${nextStatus === 'suspended' ? 'suspended' : 'reactivated'}` });
      refetch();
    } catch (e) {
      toast({ title: 'Failed to update status', description: (e as Error).message, variant: 'destructive' });
    } finally {
      setBusyId(null);
      setConfirmTarget(null);
    }
  };

  const handlePlanChange = async (tenant: TenantSummary, plan: string) => {
    setBusyId(tenant.id);
    try {
      await setTenantPlan(tenant.id, plan);
      toast({ title: `${tenant.name} moved to ${plan}` });
      refetch();
    } catch (e) {
      toast({ title: 'Failed to update plan', description: (e as Error).message, variant: 'destructive' });
    } finally {
      setBusyId(null);
    }
  };

  const handlePilotModeChange = async (tenant: TenantSummary, enabled: boolean) => {
    setBusyId(tenant.id);
    try {
      await setTenantPilotMode(tenant.id, enabled);
      toast({ title: `Pilot mode ${enabled ? 'enabled' : 'disabled'} for ${tenant.name}` });
      refetch();
    } catch (e) {
      toast({ title: 'Failed to update pilot mode', description: (e as Error).message, variant: 'destructive' });
    } finally {
      setBusyId(null);
    }
  };

  const applyDemoMode = async (tenant: TenantSummary, isDemo: boolean) => {
    setBusyId(tenant.id);
    try {
      await setTenantDemoMode(tenant.id, isDemo);
      toast({ title: `${tenant.name} set to ${isDemo ? 'Demo' : 'Live'} mode` });
      refetch();
    } catch (e) {
      toast({ title: 'Failed to update demo mode', description: (e as Error).message, variant: 'destructive' });
    } finally {
      setBusyId(null);
    }
  };

  const handleDemoModeChange = (tenant: TenantSummary, value: string) => {
    const isDemo = value === 'demo';
    if (isDemo === tenant.is_demo) return;
    if (isDemo) {
      setEnableDemoTarget(tenant); // confirm before exposing a tenant to demo tooling
    } else {
      applyDemoMode(tenant, false);
    }
  };

  const filtered = tenants.filter(t =>
    t.name.toLowerCase().includes(search.toLowerCase()) ||
    t.slug.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return <div className="flex items-center justify-center h-screen"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" /></div>;
  }

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Organisations</h1>
        <p className="text-muted-foreground text-sm">{tenants.length} total organisations</p>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center gap-4">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute start-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by name or slug..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="ps-9"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/30">
                <th className="px-6 py-3 text-start text-xs font-medium text-muted-foreground uppercase">Organisation</th>
                <th className="px-6 py-3 text-start text-xs font-medium text-muted-foreground uppercase">Plan</th>
                <th className="px-6 py-3 text-start text-xs font-medium text-muted-foreground uppercase">Mode</th>
                <th className="px-6 py-3 text-end text-xs font-medium text-muted-foreground uppercase">Total Users</th>
                <th className="px-6 py-3 text-end text-xs font-medium text-muted-foreground uppercase">MAU</th>
                <th className="px-6 py-3 text-end text-xs font-medium text-muted-foreground uppercase">WAU</th>
                <th className="px-6 py-3 text-start text-xs font-medium text-muted-foreground uppercase">Created</th>
                <th className="px-6 py-3 text-center text-xs font-medium text-muted-foreground uppercase">Pilot Mode</th>
                <th className="px-6 py-3 text-start text-xs font-medium text-muted-foreground uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filtered.map((t) => (
                <tr key={t.id} className="hover:bg-muted/20 transition-colors">
                  <td className="px-6 py-4">
                    <div className="font-medium text-foreground">{t.name}</div>
                    <div className="text-xs text-muted-foreground font-mono">/{t.slug}</div>
                  </td>
                  <td className="px-6 py-4">
                    <Select value={t.plan} onValueChange={(v) => handlePlanChange(t, v)} disabled={busyId === t.id}>
                      <SelectTrigger className="w-32 h-8 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="trial">Trial</SelectItem>
                        <SelectItem value="starter">Starter</SelectItem>
                        <SelectItem value="pro">Pro</SelectItem>
                        <SelectItem value="enterprise">Enterprise</SelectItem>
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="px-6 py-4">
                    <Select
                      value={t.is_demo ? 'demo' : 'live'}
                      onValueChange={(v) => handleDemoModeChange(t, v)}
                      disabled={busyId === t.id}
                    >
                      <SelectTrigger className="w-24 h-8 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="live">Live</SelectItem>
                        <SelectItem value="demo">Demo</SelectItem>
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="px-6 py-4 text-end font-semibold">{t.total_users}</td>
                  <td className="px-6 py-4 text-end">
                    <span className="font-semibold">{t.mau}</span>
                    {Number(t.total_users) > 0 && (
                      <span className="text-xs text-muted-foreground ms-1">
                        ({Math.round((Number(t.mau) / Number(t.total_users)) * 100)}%)
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-end font-semibold">{t.wau}</td>
                  <td className="px-6 py-4 text-muted-foreground">{format(new Date(t.created_at), 'MMM d, yyyy')}</td>
                  <td className="px-6 py-4 text-center">
                    <Switch
                      checked={t.pilot_mode}
                      onCheckedChange={(v) => handlePilotModeChange(t, v)}
                      disabled={busyId === t.id}
                    />
                  </td>
                  <td className="px-6 py-4">
                    <Button
                      size="sm"
                      variant={t.status === 'suspended' ? 'default' : 'outline'}
                      disabled={busyId === t.id}
                      onClick={() => setConfirmTarget(t)}
                    >
                      {t.status === 'suspended' ? 'Reactivate' : 'Suspend'}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="ms-2"
                      disabled={busyId === t.id || !t.is_demo}
                      title={t.is_demo ? undefined : 'Set this organisation to Demo mode to enable demo data tooling'}
                      onClick={() => setDemoDataTarget(t)}
                    >
                      Demo Data
                    </Button>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={9} className="px-6 py-10 text-center text-muted-foreground">No organisations found</td></tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <AlertDialog open={!!confirmTarget} onOpenChange={(open) => !open && setConfirmTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirmTarget?.status === 'active' ? 'Suspend' : 'Reactivate'} {confirmTarget?.name}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmTarget?.status === 'active'
                ? 'Every member of this organisation will immediately lose access to the app until it is reactivated.'
                : 'This organisation will regain access to the app immediately.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmStatusChange}>Confirm</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!enableDemoTarget} onOpenChange={(open) => !open && setEnableDemoTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Set {enableDemoTarget?.name} to Demo mode?</AlertDialogTitle>
            <AlertDialogDescription>
              Demo mode exposes this organisation to the superadmin demo-data tooling (Populate / Flush /
              Re-seed) and re-enables the built-in mock-data fallback for empty views. Only do this on an
              organisation that holds no data you need to keep.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (enableDemoTarget) applyDemoMode(enableDemoTarget, true);
                setEnableDemoTarget(null);
              }}
            >
              Set to Demo
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <DemoDataDialog tenant={demoDataTarget} onOpenChange={(open) => !open && setDemoDataTarget(null)} />
    </div>
  );
};

export default SuperadminTenants;
